import { createFileRoute } from "@tanstack/react-router";
import { editorClient } from "@/lib/editorClient";
import { usePublished } from "@op/utils";
import type { Permissions } from "@measured/puck";
import { makeExcursionPuckConfig } from "@op/puck";
import type {
  Data,
  ExcursionComponentConfig,
  ExcursionRootProps,
  Citation,
  GlossaryItem,
  ExcursionSmall,
} from "@op/puck-types";
import { useCallback, useMemo } from "react";
import { Editor } from "@/components/app/Editor";
import { useEnable } from "@/hooks/useEnable";
import { useQueryClient } from "@tanstack/react-query";
import { redirect, notFound } from "@tanstack/react-router";

async function fetchExcursionContent(excursionId: string) {
  const res = await editorClient.excursions[":excursionId"].content.$get({
    param: {
      excursionId,
    },
  });
  if (!res.ok) {
    if (res.status === 401) throw redirect({ to: "/login" });
    if (res.status === 404) throw notFound();
    throw new Error("Failed to get excursion content");
  }
  const data = await res.json();
  return data as {
    slug: string;
    data: Data<ExcursionComponentConfig, ExcursionRootProps>;
    citations: Citation[];
    glossaryItems: GlossaryItem[];
    excursions: ExcursionSmall[];
  };
}

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/$pageSlug/$versionId/excursions/$excursionId/"
)({
  component: RouteComponent,
  loader: async ({ params }) => await fetchExcursionContent(params.excursionId),
  shouldReload: () => true,
});

function RouteComponent() {
  const { versionId, organizationSlug, projectSlug, excursionId } =
    Route.useParams();
  const { slug, data, citations, glossaryItems, excursions } =
    Route.useLoaderData();

  const editorPermissions = useEnable(organizationSlug, projectSlug, [
    "admin",
    "publisher",
    "editor",
  ]);

  const published = usePublished();

  const canDelete = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canDrag = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canDuplicate = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canEdit = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canInsert = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const canSave = useMemo(() => {
    return published ? false : editorPermissions;
  }, [editorPermissions, published]);

  const permissions: Partial<Permissions> = useMemo(
    () => ({
      delete: canDelete,
      drag: canDrag,
      duplicate: canDuplicate,
      edit: canEdit,
      insert: canInsert,
    }),
    [canDelete, canDrag, canDuplicate, canEdit, canInsert]
  );

  const queryClient = useQueryClient();

  const onSave = useCallback(
    async (data: Data<ExcursionComponentConfig, ExcursionRootProps>) => {
      if (!canSave) return;
      const res = await editorClient.excursions[":excursionId"].content.$put({
        param: {
          excursionId,
        },
        json: {
          data,
        },
      });
      if (!res.ok) {
        throw new Error("Failed to save excursion content");
      }
      queryClient.invalidateQueries({
        queryKey: ["editor-layout", { versionId }],
      });
    },
    [excursionId, canSave, queryClient]
  );

  const puckConfig = useMemo(
    () => makeExcursionPuckConfig(editorClient, versionId),
    [versionId]
  );

  return (
    <Editor
      initialData={data}
      onSave={onSave}
      puckConfig={puckConfig}
      permissions={permissions}
      citations={{
        content: [],
        excursions: {
          [slug]: citations,
        },
      }}
      glossaryItems={glossaryItems}
      excursions={excursions}
      organizationSlug={organizationSlug}
      projectSlug={projectSlug}
      excursionSlug={slug}
    />
  );
}
